import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Loader2, GitCompare, Info } from 'lucide-react';
import ingestionService from '../api';

function similarityColor(val) {
  if (val >= 0.8) return 'bg-green-600 text-white';
  if (val >= 0.6) return 'bg-green-400 text-white';
  if (val >= 0.4) return 'bg-green-200 text-green-900';
  if (val >= 0.2) return 'bg-green-100 text-green-800';
  return 'bg-green-50 text-green-600';
}

function shortName(filename) {
  if (!filename) return '?';
  const base = filename.split('/').pop().split('\\').pop();
  return base.length > 18 ? base.slice(0, 16) + '…' : base;
}

export default function SimilarityView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await ingestionService.getSimilarityMatrix();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      <span className="ml-2 text-sm text-gray-500">Computing similarity matrix…</span>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
      Failed to load: {error}
      <button onClick={load} className="ml-3 underline">Retry</button>
    </div>
  );

  const docs = data?.documents || [];
  const matrix = data?.matrix || [];
  const stats = data?.stats || {};

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Document Similarity Matrix</h2>
          <p className="text-xs text-gray-500 mt-0.5">Cosine similarity between all embedded documents</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-50">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {Object.keys(stats).length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Documents', value: stats.document_count },
            { label: 'Avg Similarity', value: stats.avg_similarity != null ? `${(stats.avg_similarity * 100).toFixed(1)}%` : '—' },
            { label: 'Max Similarity', value: stats.max_similarity != null ? `${(stats.max_similarity * 100).toFixed(1)}%` : '—' },
          ].map(s => (
            <div key={s.label} className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-lg font-bold text-gray-900">{s.value ?? '—'}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
        <Info className="w-3.5 h-3.5 shrink-0" />
        <span>Similarity legend:</span>
        {[
          { cls: 'bg-green-50 border border-green-200', label: '0–19%' },
          { cls: 'bg-green-100', label: '20–39%' },
          { cls: 'bg-green-200', label: '40–59%' },
          { cls: 'bg-green-400', label: '60–79%' },
          { cls: 'bg-green-600', label: '80%+' },
        ].map(l => (
          <span key={l.label} className="flex items-center gap-1">
            <span className={`inline-block w-3 h-3 rounded-sm ${l.cls}`} />
            {l.label}
          </span>
        ))}
      </div>

      {docs.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <GitCompare className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No embedded documents found. Scan documents to generate embeddings.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-auto shadow-sm">
          <table className="text-xs border-collapse min-w-full">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 p-2 text-left text-gray-500 font-medium border-b border-r border-gray-200 min-w-[140px]">
                  Document
                </th>
                {docs.map((doc, ci) => (
                  <th
                    key={doc.id || ci}
                    className="p-2 text-center text-gray-500 font-medium border-b border-gray-200 min-w-[70px] max-w-[90px]"
                    title={doc.filename}
                  >
                    <div className="truncate max-w-[80px]">{shortName(doc.filename)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {docs.map((rowDoc, ri) => (
                <tr key={rowDoc.id || ri} className="hover:bg-gray-50/50">
                  <td
                    className="sticky left-0 z-10 bg-white p-2 font-medium text-gray-700 border-r border-gray-200 truncate max-w-[140px]"
                    title={rowDoc.filename}
                  >
                    {shortName(rowDoc.filename)}
                  </td>
                  {(matrix[ri] || []).map((val, ci) => {
                    const pct = (val * 100).toFixed(1);
                    const isHovered = hoveredCell && (hoveredCell[0] === ri || hoveredCell[1] === ci);
                    return (
                      <td
                        key={ci}
                        className={`p-2 text-center font-medium transition-opacity cursor-default border border-gray-100 ${ri === ci ? 'bg-gray-100 text-gray-400' : similarityColor(val)} ${isHovered ? 'ring-1 ring-inset ring-gray-400' : ''}`}
                        title={`${rowDoc.filename} ↔ ${docs[ci]?.filename}: ${pct}%`}
                        onMouseEnter={() => setHoveredCell([ri, ci])}
                        onMouseLeave={() => setHoveredCell(null)}
                      >
                        {ri === ci ? '—' : `${pct}%`}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hoveredCell && docs[hoveredCell[0]] && docs[hoveredCell[1]] && hoveredCell[0] !== hoveredCell[1] && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-xs text-primaryDark">
          <strong>{docs[hoveredCell[0]].filename}</strong> ↔ <strong>{docs[hoveredCell[1]].filename}</strong>:{' '}
          {((matrix[hoveredCell[0]]?.[hoveredCell[1]] || 0) * 100).toFixed(2)}% similar
        </div>
      )}
    </div>
  );
}
